/*
 * Copyright (C) 2021 IHS Markit.
 * All Rights Reserved
 *
 * NOTICE: All information contained herein is, and remains
 * the property of IHS Markit and its suppliers,
 * if any. The intellectual and technical concepts contained
 * herein are proprietary to IHS Markit and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from IHS Markit.
 */

import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../environments/environment';

@Injectable()
export class ApiService {

  URL: string;
  requestOptions: any;

  constructor(private http: HttpClient) {
    this.URL = window.location.href.includes('local') ? environment.apiRoot : window.location.origin;

    this.requestOptions = {
      headers: new HttpHeaders(this.getDefaultHeaders()),
    };
  }

  getAllIndices(page: number, pageSize: number): Observable<any> {
    return this.http.get(this.createGetPath('/screener/allIndices',
      ['page', page, 'pageSize', pageSize]), this.getDefaultHeaders());
  }

  getAllCarbonIndices(): Observable<any> {
    return this.http.get(this.createGetPath('/carbon/allIndices', []), this.getDefaultHeaders());
  }

  getAllEmixIndices(): Observable<any> {
    return this.http.get(this.createGetPath('/emix/allIndices', []), this.getDefaultHeaders());
  }

  getIndexDetailForEMIX(id: string): Observable<any> {
    return this.http.get(this.createGetPath('/emix/indexDetail', ['id', id]), this.getDefaultHeaders());
  }

  getCarbonIndexDetailsFor(id: string): Observable<any> {
    return this.http.get(this.createGetPath('/carbon/index', ['id', id]), this.getDefaultHeaders());
  }

  getISINDetailsFor(id: string): Observable<any>{
    return this.http.get(this.createGetPath('/indices/indexDetails', ['id', id]), this.getDefaultHeaders());
  }

  getIndexNameFor(id: string): Observable<any>{
    return this.http.get(this.createGetPath('/indices/indexName', ['id', id]), this.getDefaultHeaders());
  }

  createExcelFile(): Observable<any>{
    return this.http.get(this.createGetPath('/indices/createExcel', []), this.getDefaultHeaders());
  }

  getChartDataFor(id: string, period: string, timeSeries: string): Observable<any>{
    return this.http.get(this.createGetPath('/indices/chartData',
      ['id', id, 'period', period, 'timeSeries', timeSeries]), this.getDefaultHeaders());
  }

  getChartDataForEMIXFor(id: string, period: string, timeSeries: string): Observable<any>{
    return this.http.get(this.createGetPath('/emix/chartData',
      ['id', id, 'period', period, 'timeSeries', timeSeries]), this.getDefaultHeaders());
  }

  getChartDataToCompareFor(ids: string, period: string, from: string, to: string, timeSeries: string): Observable<any>{
    return this.http.get(this.createGetPath('/indices/compareChartData',
      ['ids', ids, 'period', period, 'from', from, 'to', to, 'timeSeries', timeSeries]), this.getDefaultHeaders());
  }

  getChartDataToCompareForOnEMIX(ids: string, period: string, from: string, to: string, timeSeries: string): Observable<any>{
    return this.http.get(this.createGetPath('/emix/compareChartData',
      ['ids', ids, 'period', period, 'from', from, 'to', to, 'timeSeries', timeSeries]), this.getDefaultHeaders());
  }

  getDataToDownload(id: string, period: string, timeSeries: string, fileName: string): void{
    // return this.http.get(this.createGetPath('/indices/downloadData',
    //   ['id', id, 'period', period, 'timeSeries', timeSeries, 'fileName', fileName]), this.getDefaultHeaders());
    window.open(this.createGetPath('/indices/downloadData',
      ['id', id, 'period', period, 'timeSeries', timeSeries, 'fileName', fileName]));
  }

  getDataToDownloadForCustom(id: string, from: string, to: string, timeSeries: string, fileName: string): void{
    // return this.http.get(this.createGetPath('/indices/downloadDataForCustom',
    //   ['id', id, 'from', from, 'to', to, 'timeSeries', timeSeries, 'fileName', fileName]), this.getDefaultHeaders());
    window.open(this.createGetPath('/indices/downloadDataForCustom',
      ['id', id, 'from', from, 'to', to, 'timeSeries', timeSeries, 'fileName', fileName]));
  }

  getConstituentDataToDownload(id: string, assetType: string, fileName: string): void{
    // return this.http.get(this.createGetPath('/indices/downloadConstituentData',
    //   ['id', id, 'assetType', assetType, 'fileName', fileName]), this.getDefaultHeaders());
    window.open(this.createGetPath('/indices/downloadConstituentData',
      ['id', id, 'assetType', assetType, 'fileName', fileName]));
  }

  getDataToDownloadForEMIX(id: string, period: string, timeSeries: string, fileName: string): Observable<any>{
    return this.http.get(this.createGetPath('/emix/downloadData',
      ['id', id, 'period', period, 'timeSeries', timeSeries, 'fileName', fileName]), this.getDefaultHeaders());
  }

  getDataToDownloadForCarbon(id: string, period: string, timeSeries: string, fileName: string): void{
    // return this.http.get(this.createGetPath('/carbon/downloadData',
    //   ['id', id, 'period', period, 'timeSeries', timeSeries, 'fileName', fileName]), this.getDefaultHeaders());
    window.open(this.createGetPath('/carbon/downloadData',
      ['id', id, 'period', period, 'timeSeries', timeSeries, 'fileName', fileName]));
  }

  getDataToDownloadForCustomForCarbon(id: string, from: string, to: string, timeSeries: string, fileName: string): void{
    // return this.http.get(this.createGetPath('/carbon/downloadDataForCustom',
    //   ['id', id, 'from', from, 'to', to, 'timeSeries', timeSeries, 'fileName', fileName]), this.getDefaultHeaders());
    window.open(this.createGetPath('/carbon/downloadDataForCustom',
      ['id', id, 'from', from, 'to', to, 'timeSeries', timeSeries, 'fileName', fileName]));
  }

  getDataToDownloadForCustomForEMIX(id: string, from: string, to: string, timeSeries: string, fileName: string): Observable<any>{
    return this.http.get(this.createGetPath('/emix/downloadDataForCustom',
      ['id', id, 'from', from, 'to', to, 'timeSeries', timeSeries, 'fileName', fileName]), this.getDefaultHeaders());
  }

  getDataToDownloadForIndices(ids: string, period: string, from: string, to: string, timeSeries: string, fileName: string): void{
    // return this.http.get(this.createGetPath('/indices/downloadDataForIndices',
    //   ['ids', ids, 'period', period, 'from', from, 'to', to, 'timeSeries', timeSeries, 'fileName', fileName]), this.getDefaultHeaders());
    window.open(this.createGetPath('/indices/downloadDataForIndices',
      ['ids', ids, 'period', period, 'from', from, 'to', to, 'timeSeries', timeSeries, 'fileName', fileName]));
  }

  getDataToDownloadForIndicesForEMIX(ids: string, period: string, from: string, to: string, timeSeries: string,
                                     fileName: string): Observable<any>{
    return this.http.get(this.createGetPath('/emix/downloadDataForIndices',
      ['ids', ids, 'period', period, 'from', from, 'to', to, 'timeSeries', timeSeries, 'fileName', fileName]), this.getDefaultHeaders());
  }

  getChartDataForCarbonIndexFor(id: string, period: string, timeSeries: string): Observable<any>{
    return this.http.get(this.createGetPath('/carbon/chartData',
      ['id', id, 'period', period, 'timeSeries', timeSeries]), this.getDefaultHeaders());
  }

  getCustomChartDataFor(id: string, from: string, to: string, timeSeries: string): Observable<any>{
    return this.http.get(this.createGetPath('/indices/customChartData',
      ['id', id, 'from', from, 'to', to, 'timeSeries', timeSeries]), this.getDefaultHeaders());
  }

  getCustomEMIXChartDataFor(id: string, from: string, to: string, timeSeries: string): Observable<any>{
    return this.http.get(this.createGetPath('/emix/customChartData',
      ['id', id, 'from', from, 'to', to, 'timeSeries', timeSeries]), this.getDefaultHeaders());
  }

  getCustomChartDataForCarbonIndexFor(id: string, from: string, to: string, timeSeries: string): Observable<any>{
    return this.http.get(this.createGetPath('/carbon/customChartData',
      ['id', id, 'from', from, 'to', to, 'timeSeries', timeSeries]), this.getDefaultHeaders());
  }

  getConstituentData(id: string): Observable<any>{
    return this.http.get(this.createGetPath('/indices/constituentData',
      ['id', id]), this.getDefaultHeaders());
  }
  getConstituentDataForUCITS(id: string): Observable<any>{
    return this.http.get(this.createGetPath('/indices/constituentDataForUCITS',
      ['id', id]), this.getDefaultHeaders());
  }

  getConstituentDataForCarbon(id: string): Observable<any>{
    return this.http.get(this.createGetPath('/carbon/constituentData', ['id', id]), this.getDefaultHeaders());
  }

  getConstituentDataForEMIX(eiCode: string): Observable<any>{
    return this.http.get(this.createGetPath('/emix/constituentData', ['eiCode', eiCode]), this.getDefaultHeaders());
  }

  getConstituentDetails(id: string, assetType: string): Observable<any>{
    return this.http.get(this.createGetPath('/indices/fetchConstituents', ['id', id, 'assetType', assetType]), this.getDefaultHeaders());
  }

  getDocumentDetails(configType: string): Observable<any>{
    return this.http.get(this.createGetPath('/sftp/documentConfig', ['configType', configType]), this.getDefaultHeaders());
  }

  getFileAsPDF(configType: string, fileName: string): Observable<any>{
    return this.http.get(this.createGetPath('/sftp/downloadPDF',
      ['configType', configType, 'fileName', fileName]), this.getHeadersForDownload(fileName));
  }

  getHeaderFooter(): Observable<any> {
    // return this.http.post('https://qa.www.spglobal.com/spdji/en/about-us/', this.getDefaultHeaders());
    return this.http.post('https://www.spglobal.com/spdji/en/about-us/', this.getDefaultHeaders());
  }

  getIndicesDump(): Observable<any>{
    return this.http.get(this.createGetPath('/sftp/indicesDump', []), this.getDefaultHeaders());
  }

  getHolidayCalendar(): Observable<any>{
    return this.http.get(this.createGetPath('/search/holidays', []), this.getDefaultHeaders());
  }

  getEuriborValues(): Observable<any>{
    return this.http.get(this.createGetPath('/search/euriborValues', []), this.getDefaultHeaders());
  }

  getSOFRAIValueFor(startDate: string, endDate: string): Observable<any>{
    return this.http.get('https://markets.newyorkfed.org/api/rates/secured/sofrai/search.json?type=rate&startDate=' + startDate + '&endDate=' + endDate, this.getDefaultHeaders());
  }

  getHeadlineIndices(): Observable<any>{
    return this.http.get(this.createGetPath('/sftp/headlineIndices', []), this.getDefaultHeaders());
  }

  getHeadlineIndicesFromScreener(configType: string): Observable<any>{
    return this.http.get(this.createGetPath('/sftp/screenerHeadlineIndices',
      ['configType', configType]), this.getDefaultHeaders());
  }

  getHeadersForDownload(fileName: string): any{
    if (fileName.includes('pdf') || fileName.includes('xls')) {
      return {
        responseType: 'arraybuffer' as 'json',
      };
    }else{
      return {
        responseType: 'text',
      };
    }
  }

  getDefaultHeaders(): any {
    return {
      'Content-Type': 'application/json'
    };
  }

  getSearchResult(searchString: string): Observable<any>{
    return this.http.get(this.createGetPath('/search/globalSearch', ['searchString', searchString]), this.getDefaultHeaders());
  }

  getDocStatus(): Observable<any>{
    return this.http.get(this.createGetPath('/search/docStatus', []), this.getDefaultHeaders());
  }

  getS3Objects(): Observable<any>{
    return this.http.get(this.createGetPath('/search/s3Objects', []), this.getDefaultHeaders());
  }

  getTextFileFromS3(fileName: string): Observable<any>{
    return this.http.get(this.createGetPath('/search/fileFromObject',
      ['fileName', fileName]), this.getHeadersForDownload(fileName));
  }

  getPDFFileFromS3(fileName: string): Observable<any>{
    return this.http.get(this.createGetPath('/search/fileFromObject',
      ['fileName', fileName]), this.getHeadersForDownload(fileName));
  }

  getIndexDetail(fetchBy: string, term: string): Observable<any>{
    return this.http.get(this.createGetPath('/screener/getIndex',
      ['fetchBy', fetchBy, 'term', term]), this.getDefaultHeaders());
  }

  createGetPath(url: string, argsArray: any[]): string {
    let args = '';
    for (let i = 0; i < argsArray.length; i++) {
      args += argsArray[i] + '=' + argsArray[++i] + '&';
    }

    return this.URL.concat(url.concat('?' + args));
  }
}
